import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type Board, useBoardDetail } from '@/entities/board';
import { useErrorMessage } from '@/shared/i18n/errors';
import { Dialog, LoadingState } from '@/shared/ui';

import { BoardAppearanceDialog } from '@/features/board-manage/BoardAppearanceDialog';

import { BoardSettingsDialog } from './BoardSettingsDialog';

/**
 * Settings of a board opened from its catalog card: the same dialog the board page opens, fed by
 * the board's own detail (current values and the permissions the server reports), so what a
 * person may change here is exactly what they may change inside the board.
 */
export function BoardCardSettings({ board, onClose }: { board: Board; onClose: () => void }) {
  const { t } = useTranslation();
  const detail = useBoardDetail(board.id);
  const message = useErrorMessage();
  const [appearance, setAppearance] = useState(false);
  if (!detail.data) {
    return (
      <Dialog title={t('boards.settings')} onClose={onClose}>
        {detail.isError ? <p role="alert">{message(detail.error)}</p> : <LoadingState />}
      </Dialog>
    );
  }
  if (appearance) {
    return <BoardAppearanceDialog board={detail.data.board} onClose={onClose} />;
  }
  return (
    <BoardSettingsDialog
      key={detail.data.board.version}
      board={detail.data.board}
      columns={detail.data.columns}
      role={detail.data.role}
      permissions={detail.data.permissions}
      onClose={onClose}
      onDeleted={onClose}
      onAppearance={() => {
        setAppearance(true);
      }}
    />
  );
}
